import { Settings } from '@mui/icons-material';
import NavigationIcon from '@mui/icons-material/Navigation';
import {
  Alert,
  Box,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BottomNav from '@/components/BottomNav';
import { LoadingSpinner } from '@/components/Loading';
import DateRange from '@/components/Schedule/DateRange';
import RunOfShowCell from '@/components/Schedule/RunOfShowCell';
import { useAuth } from '@/contexts/AuthContext';
import useFitText from '@/hooks/useFitText';
import useTopPositionChange from '@/hooks/usePositionChange';
import { api } from '@/utils/api';
import { parseHMS } from '@/utils/datetime';
import { findCurrentRunId, getCurrentRunColor, getDaySeparatorColor } from '@/utils/schedule';

const ICON_BREAKPOINT = 'md';

/**
 * How far the current run's bookmark pokes out past the left edge of the schedule's rows.
 * Kept narrow on mobile, where that margin is scarce; from breakpoint up it gets enough room
 * to hold an icon. In `rem` rather than `em` so the strip and the gutter the
 * TableContainer reserves for it come out the same width, despite the cells' smaller font.
 */
const BOOKMARK_WIDTH = { xs: '0.75rem', [ICON_BREAKPOINT]: '1.75rem' };

/** Turns a single-width `sx` value into a responsive one, one entry per bookmark breakpoint */
const perBookmarkWidth = template =>
  Object.fromEntries(
    Object.entries(BOOKMARK_WIDTH).map(([breakpoint, width]) => [breakpoint, template(width)]),
  );

/**
 * Marks the current run by running its color out past the left edge of its row.
 */
function Bookmark({ color }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: '100%',
        bottom: 0,
        display: 'flex',
        // The icon is drawn at a fixed size, so keep a short row from letting it bleed out
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        width: BOOKMARK_WIDTH,
        // Match the table's own rounding
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
        backgroundColor: color,
        color: theme.palette.getContrastText(color),
      }}
    >
      <NavigationIcon
        titleAccess={t('schedule.currentRun')}
        sx={{
          display: { xs: 'none', [ICON_BREAKPOINT]: 'block' },
          fontSize: '1.25rem',
          transform: 'rotate(90deg)',
        }}
      />
    </Box>
  );
}

Bookmark.propTypes = {
  color: PropTypes.string.isRequired,
};

/** Containing block for the current run's `Bookmark` */
const bookmarkedCellSx = { position: 'relative' };

function ScheduleTableRow({
  daySeparatorColor,
  currentRunColor,
  isNewDay,
  isCurrentRun,
  run,
  runOfShow,
  showHost,
  stationsCount,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // The current run outranks a run's own color: viewers need to find where the schedule is at
  const rowColor = (isCurrentRun ? currentRunColor : run.backgroundColor) || null;
  const cellSx = {
    ...(rowColor && {
      backgroundColor: rowColor,
      color: theme.palette.getContrastText(rowColor),
    }),
    ...(isCurrentRun && { fontWeight: 'bold' }),
  };

  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  useEffect(() => {
    setHeaderHeight(headerRef?.current?.clientHeight ?? 0);
  }, [i18n.language, isMobile]);

  return (
    <>
      {isNewDay && (
        <TableHead>
          <TableRow ref={headerRef}>
            <TableCell
              // Run of show swaps the platform column out for extra info, so the count holds
              colSpan={showHost ? 7 : 6}
              sx={{
                // Lines up with the TableContainer's own rounded corner (hidden by the
                // bookmark's left padding), but only on the table's actual first row.
                // Can't do only the first row as scrolling would cause a lower day separator to
                // break the rounding again when stickied. So round both top corners for consistency
                borderTopLeftRadius: theme.shape.borderRadius,
                borderTopRightRadius: theme.shape.borderRadius,
                backgroundColor: daySeparatorColor,
                fontSize: theme.typography.h6.fontSize,
                fontWeight: 'bold',
                color: theme.palette.getContrastText(daySeparatorColor),
                textTransform: 'capitalize',
              }}
            >
              {run.scheduledStartTime.toLocaleDateString(i18n.language, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </TableCell>
          </TableRow>
          <TableRow>
            {/* Large enough to fit "Start Time" (let it wrap in french) */}
            <TableCell style={{ top: `${headerHeight}px`, minWidth: '7.1em' }}>
              {t('common.start.time')}
            </TableCell>
            {/* Large enough to fit "Nom du jeu" on 2 lines on mobile */}
            <TableCell style={{ top: `${headerHeight}px`, minWidth: '4em' }}>
              {t('run.name')}
            </TableCell>
            <TableCell style={{ top: `${headerHeight}px` }}>{t('run.category')}</TableCell>
            {/* Each station carries its own platform+emulator in run of show */}
            {!runOfShow && (
              <TableCell style={{ top: `${headerHeight}px` }}>{t('run.platform')}</TableCell>
            )}
            <TableCell style={{ top: `${headerHeight}px` }}>{t('run.estimate')}</TableCell>
            <TableCell style={{ top: `${headerHeight}px` }}>{t('common.players')}</TableCell>
            {runOfShow && (
              <TableCell style={{ top: `${headerHeight}px` }}>{t('run.extraInfo')}</TableCell>
            )}
            {showHost && (
              <TableCell style={{ top: `${headerHeight}px` }}>{t('run.host')}</TableCell>
            )}
          </TableRow>
        </TableHead>
      )}
      <TableRow>
        <TableCell
          sx={{
            ...cellSx,
            ...(isCurrentRun && bookmarkedCellSx),
          }}
        >
          {isCurrentRun && <Bookmark color={currentRunColor} />}
          {run.scheduledStartTime.toLocaleTimeString(i18n.language, {
            timeStyle: 'short',
            hour12: true,
          })}
        </TableCell>
        <TableCell sx={cellSx}>{run.gameName}</TableCell>
        <TableCell sx={cellSx}>{run.category}</TableCell>
        {!runOfShow && <TableCell sx={cellSx}>{run.platform}</TableCell>}
        <TableCell sx={cellSx}>{run.estimate}</TableCell>
        <TableCell sx={cellSx}>
          {runOfShow ? (
            <RunOfShowCell run={run} stationsCount={stationsCount} />
          ) : (
            run.combinedPlayers
          )}
        </TableCell>
        {runOfShow && (
          <TableCell sx={{ ...cellSx, whiteSpace: 'pre-line' }}>{run.extraInfo}</TableCell>
        )}
        {showHost && <TableCell sx={cellSx}>{run.host}</TableCell>}
      </TableRow>
    </>
  );
}

ScheduleTableRow.propTypes = {
  currentRunColor: PropTypes.string.isRequired,
  daySeparatorColor: PropTypes.string.isRequired,
  isCurrentRun: PropTypes.bool.isRequired,
  isNewDay: PropTypes.bool.isRequired,
  run: PropTypes.object.isRequired,
  runOfShow: PropTypes.bool.isRequired,
  showHost: PropTypes.bool.isRequired,
  stationsCount: PropTypes.number.isRequired,
};

/**
 * The public schedule of an event, in one of two readings of the same rows.
 *
 * `runOfShow` is the volunteer's reading: the platform column moves into the players column,
 * which becomes one line per station (who sits there, on what, captured how), and the run's
 * extra info comes along for anything the columns don't cover.
 *
 * @param {object} props
 * @param [props.eventSchedule] - Pre-fetched bundle. Fetched from the URL's slug when missing
 * @param [props.runOfShow] - Lay the rows out for the volunteers running the stations
 */
export default function SchedulePage({
  eventSchedule: initialEventSchedule = null,
  runOfShow = false,
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [schedule, setSchedule] = useState(null);
  const [scheduleEvent, setScheduleEvent] = useState(null);
  const [runs, setRuns] = useState([]);
  const [eventSchedule, setEventSchedule] = useState(initialEventSchedule);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { groups } = useAuth();

  const titleKey = runOfShow ? 'runOfShow.scheduleTitle' : 'schedule.title';
  const titleText = t(titleKey, { name: scheduleEvent?.name });
  const { ref: titleRef, fontSize: titleFontSize } = useFitText(titleText, { maxHeightEm: 8 });

  const tableRef = useRef(null);
  const [tableHeight, setTableHeight] = useState('auto');
  useTopPositionChange(tableRef, top =>
    setTableHeight(`calc(100dvh - ${top}px - ${theme.spacing(3)})`),
  );

  useEffect(() => {
    // TODO: This could be SSR, so that the eventSchedule prop is never null
    if (eventSchedule) return;
    api
      .get(`/api/v2/schedules/by_event_slug/${router.query.slug}/`)
      .then(response => {
        setEventSchedule(response.data);
        setIsLoading(false);
      })
      // eslint-disable-next-line no-console -- TODO: really need that easy-to-use global snackbar alert
      .catch(console.error);
  }, [router.query.slug, eventSchedule]);

  useEffect(() => {
    const canonicalSlug = eventSchedule?.event?.slug;
    // Slugs are stored case-sensitively, send the user to the canonical URL
    if (!canonicalSlug || !router.query.slug || canonicalSlug === router.query.slug) return;
    router.replace(`/schedule/${canonicalSlug}${runOfShow ? '/run-of-show' : ''}`);
  }, [eventSchedule, router, runOfShow]);

  useEffect(() => {
    if (!eventSchedule) return;
    const prepared = eventSchedule.runs.map(run => ({
      ...run,
      player1: run.player1 || '',
      player2: run.player2 || '',
      player3: run.player3 || '',
      player4: run.player4 || '',
      host: run.host || '',
      extraInfo: run.extraInfo || '',
      stations: run.stations ?? [],
      combinedPlayers: [run.player1, run.player2, run.player3, run.player4]
        .filter(Boolean)
        .join(', '),
    }));
    setRuns(prepared);
    setSchedule(eventSchedule.schedule);
    setScheduleEvent(eventSchedule.event);
  }, [eventSchedule]);

  const scheduledRuns = useMemo(() => {
    if (!scheduleEvent || !schedule) return [];

    let currentStartTime = new Date(scheduleEvent.startAt);

    return runs.map(run => {
      const runStartTime = new Date(currentStartTime);
      const runDuration = parseHMS(run.estimate) * 1000;

      currentStartTime = new Date(
        currentStartTime.getTime() + runDuration + run.setupTime * 60 * 1000,
      );

      return { ...run, scheduledStartTime: runStartTime };
    });
  }, [runs, schedule, scheduleEvent]);

  const renderTableContent = () => {
    // Don't take up horizontal space with an entirely empty column
    const showHost = scheduledRuns.some(run => run.host);
    const daySeparatorColor = getDaySeparatorColor(schedule, theme);
    const currentRunColor = getCurrentRunColor(schedule, theme);
    const currentRunId = findCurrentRunId(scheduledRuns, run => run.scheduledStartTime);
    let previousDate = null;

    const rows = scheduledRuns.map(run => {
      const runDate = run.scheduledStartTime.toDateString();
      const isNewDay = runDate !== previousDate;
      previousDate = runDate;

      return {
        isCurrentRun: run.id === currentRunId,
        isNewDay,
        run,
      };
    });

    return rows.map(({ isCurrentRun, isNewDay, run }) => (
      <ScheduleTableRow
        key={run.id}
        daySeparatorColor={daySeparatorColor}
        currentRunColor={currentRunColor}
        isCurrentRun={isCurrentRun}
        isNewDay={isNewDay}
        run={run}
        runOfShow={runOfShow}
        showHost={showHost}
        stationsCount={schedule?.stationsCount ?? 0}
      />
    ));
  };

  if (isLoading) {
    return <LoadingSpinner setIsLoading={setIsLoading} />;
  }

  if (!eventSchedule) {
    return (
      <Typography variant="h2" sx={{ textAlign: 'center' }}>
        {t('schedule.doesNotExist', { slug: router.query.slug })}
      </Typography>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ paddingTop: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            ref={titleRef}
            variant="h2"
            component="h1"
            className="font-bebas"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: titleFontSize,
              whiteSpace: 'nowrap',
            }}
          >
            {titleText}
          </Typography>
          {groups.includes('Editor') && (
            <Tooltip title={t('schedule.edit', { slug: eventSchedule.event.slug })}>
              <IconButton
                href={`/schedule/${eventSchedule.event.slug}/edit`}
                sx={{ marginLeft: { xs: 1, sm: 2, md: 3, lg: 4 } }}
              >
                <Settings sx={{ fontSize: { xs: '1.5em', sm: '2.5em', md: '3em' } }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <DateRange start={new Date(scheduleEvent?.startAt)} end={new Date(scheduleEvent?.endAt)} />
        {!schedule?.isPublic && (
          <Alert severity="warning" sx={{ marginBottom: 2 }}>
            {t('schedule.notPublic')}
          </Alert>
        )}
        <TableContainer
          ref={tableRef}
          component={Paper}
          style={{ maxHeight: tableHeight }}
          sx={{
            // Room for the bookmark to poke into, since the container clips whatever overflows
            // it: taken back out of the page gutter so the table doesn't shift, grown by as much
            // on the right so it stays centered (TableContainer is a fixed `width: 100%`, which
            // the negative margin would otherwise pull in), and left unpainted so the strip
            // doesn't read as a border running down the whole table.
            width: perBookmarkWidth(width => `calc(100% + ${width})`),
            marginLeft: perBookmarkWidth(width => `-${width}`),
            paddingLeft: BOOKMARK_WIDTH,
            backgroundClip: 'content-box',
          }}
        >
          <Table stickyHeader size={isMobile ? 'small' : undefined}>
            {renderTableContent()}
          </Table>
        </TableContainer>
      </Box>
      {isMobile && <BottomNav />}
    </Container>
  );
}

SchedulePage.propTypes = {
  eventSchedule: PropTypes.object,
  runOfShow: PropTypes.bool,
};
